import { useMemo } from 'react';

import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';

import { ItemAvailabilityStatus } from 'enums/menu';
import { useMenuContext } from 'state/menu';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';

export interface MinimalRecentEntryAvailability {
  itemAvailabilityStatus: ItemAvailabilityStatus;
  dayParts: string[];
}

const isMinimalCartEntryAvailableOneMenu = (
  cartEntry: ICartEntry,
  getOneMenuItemAvailability: ReturnType<typeof useMenuContext>['getOneMenuItemAvailability']
): boolean => {
  if (cartEntry.type === MinimalCartEntryType.extra) {
    return true;
  }

  if (
    getOneMenuItemAvailability(cartEntry.id).availabilityStatus !== ItemAvailabilityStatus.AVAILABLE
  ) {
    return false;
  }

  return cartEntry.children.every(child =>
    isMinimalCartEntryAvailableOneMenu(child, getOneMenuItemAvailability)
  );
};

export const useMinimalRecentEntryAvailability = (entries: ICartEntry[]) => {
  const { getOneMenuItemAvailability } = useMenuContext();
  const { indexedMenu } = useUnifiedMenuContext();

  const availability = useMemo<MinimalRecentEntryAvailability[]>(() => {
    return entries.map(entry => {
      if (entry.type === MinimalCartEntryType.extra) {
        return {
          itemAvailabilityStatus: ItemAvailabilityStatus.CART_EXTRA,
          dayParts: [],
        };
      }

      const menuEntry = indexedMenu.find(entry.id);
      const dayParts = [...(menuEntry?.dayParts ?? [])];
      let itemAvailabilityStatus = getOneMenuItemAvailability(entry.id).availabilityStatus;

      if (itemAvailabilityStatus === ItemAvailabilityStatus.AVAILABLE) {
        if (!isMinimalCartEntryAvailableOneMenu(entry, getOneMenuItemAvailability)) {
          itemAvailabilityStatus = ItemAvailabilityStatus.UNAVAILABLE;
        }
      }

      return {
        itemAvailabilityStatus,
        dayParts,
      };
    });
  }, [entries, getOneMenuItemAvailability, indexedMenu]);

  const hasSomeAvailableEntry = useMemo(
    () =>
      availability.some(
        entryAvailability =>
          entryAvailability.itemAvailabilityStatus === ItemAvailabilityStatus.AVAILABLE
      ),
    [availability]
  );

  return {
    availability,
    checkingAvailability: false,
    hasSomeAvailableEntry,
  };
};
